import React, { useState } from "react";
import { Menu, Typography, Space, Dropdown, Button, Tooltip, message, Modal } from "antd";
import { ExportOutlined } from "@ant-design/icons";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { connect, useSelector } from "react-redux";
import { createStructuredSelector } from "reselect";
import styled, { keyframes } from "styled-components";
import { selectUser, selectTableColumns, selectSorting } from "../../store/selector";

import { Routes } from "../../constant/routes";
import SuccessMessage from "../../component/customComponent/customMessages/SuccessMessage";
import ACTIONS from "../../store/action";
import { GRAY7, GRAY_400, GRAY_500, LIGHT_BLUE_2, PRIMARY_700, WHITE, BLACK } from "../../constant/colors";
import { fontWeights } from "../../constant/styles";
import { showExportConfirmModal } from "../../utils/export";
import { FILE_TYPES, MAX_EXPORT_RECORDS, LIST_TYPES, EXCLUDED_EXPORT_COLUMNS } from "../../constant/export";
import { selectedViewTypes } from "../../constant/table";
import { useExportWithTracking } from "../../hooks/useExportWithTracking";
import { ReactComponent as ActionEditIcon } from "../../assets/svg/actions-edit-new.svg";
import { ReactComponent as ActionUrlIcon } from "../../assets/svg/actions-url.svg";
import { ReactComponent as ActionDeleteIcon } from "../../assets/svg/actions-delete-new.svg";
import { ReactComponent as ExportLoadingIcon } from "../../assets/svg/export-loading-icon.svg";
import { ReactComponent as ArrowBackIcon } from "../../assets/svg/arrow-back-icon.svg";
import { formPopupsContentKeys } from "../../constant/formPopupsContentKeys";
import { ThreeDots } from "../customComponent/ThreeDots";
import { ActionsMenuStyle } from "./SavedListActionsMenuStyle";
import { parseSearchFormParams } from "../../api/api";

const { Text } = Typography;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(-360deg);
  }
`;

const RotatingIconWrapper = styled.div`
  animation: ${rotate} 1s linear infinite;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledMenu = styled(Menu)`
  ${ActionsMenuStyle}

  &.export-menu .ant-dropdown-menu-item:last-child {
    border-top: none;
  }
`;

const StyledButton = styled(Button)`
  border-radius: 15px;
  padding: 5px;

  :active,
  :focus,
  :hover {
    background: ${LIGHT_BLUE_2};
    span {
      background-color: ${PRIMARY_700};
    }
  }
`;

const ActionsMenuComponent = ({
  t,
  user,
  source,
  record,
  recordCount,
  deleteAction,
  openEditSavedListPopup,
  populateSavedListPopup
}) => {
  const isOwner = user?.id === record?.user;
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [modal, contextHolder] = Modal.useModal();

  const tableColumns = useSelector(selectTableColumns);
  const currentSorting = useSelector(selectSorting);
  const { exportWithTracking } = useExportWithTracking();

  // recordCount can be undefined (still loading), null (failed), or a number
  const isRecordCountLoading = recordCount === undefined;
  const isExportDisabled =
    isRecordCountLoading || recordCount === null || recordCount > MAX_EXPORT_RECORDS || recordCount === 0;

  const performExport = async (fileType) => {
    const savedListParams = typeof record.params === "string" ? JSON.parse(record.params) : record.params || {};
    const savedListFilters = savedListParams.filters || {};
    const savedListSelectedView = record.selectedView;

    const visibleColumns =
      savedListParams.selectedColumns && savedListParams.selectedColumns.length > 0
        ? savedListParams.selectedColumns
        : Object.keys(tableColumns[savedListSelectedView] || {}).filter(
            (key) => tableColumns[savedListSelectedView][key]?.mandatory === true
          );

    const filteredVisibleColumns = visibleColumns.filter((col) => !EXCLUDED_EXPORT_COLUMNS.includes(col));
    const staticColumns = Object.keys(tableColumns[savedListSelectedView] || {}).filter(
      (key) => tableColumns[savedListSelectedView][key]?.mandatory === true
    );
    const allColumns = [...new Set([...filteredVisibleColumns, ...staticColumns])];

    const transformedFilters = parseSearchFormParams({
      selectedView: savedListSelectedView,
      page: 1,
      limit: MAX_EXPORT_RECORDS,
      filters: savedListFilters
    });

    // Show loading message before export
    message.open({
      content: (
        <Space>
          <RotatingIconWrapper style={{ height: "36px" }}>
            <ExportLoadingIcon style={{ width: 36, height: 36 }} />
          </RotatingIconWrapper>
          <Text style={{ color: BLACK }}>{t("export.preparing_export")}</Text>
        </Space>
      ),
      key: "exportMessage",
      duration: 0,
      className: "export-loading-message"
    });

    await exportWithTracking(
      {
        listType: LIST_TYPES.SAVED_LIST,
        savedListId: record.id,
        selectedView: savedListSelectedView,
        filters: transformedFilters.filters || {},
        sorting: currentSorting || {},
        visibleColumns: allColumns,
        fileType
      },
      {
        pageKey: `${savedListSelectedView}`,
        contentType: selectedViewTypes[savedListSelectedView]?.description,
        recordCount,
        fileType,
        sorting: currentSorting || {},
        visibleColumns: allColumns,
        filters: transformedFilters.filters || {},
        isSavedList: true,
        savedListId: record.id,
        savedListName: record.name,
        savedListOriginBucket: savedListSelectedView
      },
      {
        showLoadingMessage: true,
        loadingMessageKey: "exportMessage",
        loadingDelay: 500
      }
    );
  };

  const handleExport = (fileType) => {
    setDropdownVisible(false);
    setShowExportMenu(false);

    showExportConfirmModal(modal, {
      recordCount,
      fileType,
      t,
      onConfirm: performExport
    });
  };

  const handleVisibleChange = (visible) => {
    setDropdownVisible(visible);
    if (!visible) {
      setShowExportMenu(false);
    }
  };

  const actionsMenuItems = [
    {
      id: `${source}_options_edit`,
      key: "edit",
      title: t("savedList.actions_menu.edit"),
      Icon: ActionEditIcon,
      show: isOwner && record.isValid,
      actions: () => {
        populateSavedListPopup(record);
        openEditSavedListPopup();
      }
    },
    {
      key: "copy",
      title: t("savedList.actions_menu.copy"),
      id: `saved_lists_copy_list_link`,
      Icon: ActionUrlIcon,
      show: record.isValid,
      actions: () => {
        const { id } = record;
        const url = `${window.location.origin}${Routes.FILTER_LIST}/${id}`;
        navigator.clipboard.writeText(url);
        SuccessMessage(t("savedList.copy_action"));
      }
    },
    {
      key: "export",
      title: t("savedList.actions_menu.export"),
      Icon: ExportOutlined,
      id: `saved_lists_export_list`,
      show: record.isValid,
      disabled: isExportDisabled,
      hasSubmenu: true,
      disabledTooltip: (() => {
        if (isRecordCountLoading) return null;
        if (recordCount > MAX_EXPORT_RECORDS) {
          return t("export.record_limit_exceeded_tooltip", { limit: MAX_EXPORT_RECORDS.toLocaleString() });
        }
        return null;
      })(),
      actions: () => {
        if (!isExportDisabled) {
          setShowExportMenu(true);
        }
      }
    },
    {
      id: `${source}_options_delete`,
      key: "delete",
      title: t("savedList.actions_menu.delete"),
      Icon: ActionDeleteIcon,
      show: isOwner,
      actions: () => {
        deleteAction(record);
      }
    }
  ];

  const itemsToShow = actionsMenuItems.filter((item) => item.show);

  const exportMenuItems = (
    <StyledMenu style={{ width: "89px" }} className="export-menu">
      <Menu.Item key="back" onClick={() => setShowExportMenu(false)}>
        <Space>
          <ArrowBackIcon style={{ width: "6px", height: "10px", color: GRAY_500 }} />
        </Space>
      </Menu.Item>
      <Menu.Item key={FILE_TYPES.CSV} onClick={() => handleExport(FILE_TYPES.CSV)} id="saved_lists_export_csv">
        <Text>{t("export.csv_option")}</Text>
      </Menu.Item>
      <Menu.Item key={FILE_TYPES.XLSX} onClick={() => handleExport(FILE_TYPES.XLSX)} id="saved_lists_export_xlsx">
        <Text>{t("export.xlsx_option")}</Text>
      </Menu.Item>
    </StyledMenu>
  );

  const mainMenuItems = (
    <StyledMenu>
      {itemsToShow.map(({ id, key, actions, title, Icon, disabled = false, disabledTooltip, hasSubmenu }) => {
        const menuItemContent = (
          <Menu.Item
            id={id}
            key={key}
            onClick={() => !disabled && actions(record)}
            disabled={disabled}
            style={{
              ...(key === "delete" ? { borderTop: "1px solid " + GRAY7 } : {}),
              ...(disabled ? { cursor: "not-allowed" } : {})
            }}
          >
            <Space style={{ justifyContent: "space-between", width: "100%" }}>
              <Text style={disabled ? { color: GRAY_400 } : {}}>
                {Icon && (
                  <span
                    style={{
                      marginRight: 9
                    }}
                  >
                    <Icon style={disabled ? { color: GRAY_400 } : {}} />
                  </span>
                )}
                {title}
              </Text>
              {hasSubmenu && (
                <ArrowBackIcon
                  style={{
                    width: "6px",
                    height: "10px",
                    transform: "rotate(180deg)",
                    color: disabled ? GRAY_400 : GRAY_500
                  }}
                />
              )}
            </Space>
          </Menu.Item>
        );

        return disabled && disabledTooltip ? (
          <Tooltip
            key={key}
            title={disabledTooltip}
            placement="left"
            overlayInnerStyle={{
              padding: "10px 10px",
              width: "280px",
              color: BLACK,
              backgroundColor: WHITE,
              fontWeight: fontWeights.regular,
              fontSize: "12px",
              borderRadius: "8px"
            }}
            overlayStyle={{ maxWidth: "300px" }}
            color={WHITE}
          >
            <div>{menuItemContent}</div>
          </Tooltip>
        ) : (
          menuItemContent
        );
      })}
    </StyledMenu>
  );

  const overlayItems = showExportMenu ? exportMenuItems : mainMenuItems;

  const dropDown = Boolean(isOwner || record.isValid) && (
    <Dropdown
      dropdownRender={() => overlayItems}
      placement="bottomRight"
      trigger={["click"]}
      open={dropdownVisible}
      onOpenChange={handleVisibleChange}
    >
      <div style={{ marginRight: "20px" }}>
        <StyledButton type="text" id="saved_lists_options_link">
          <ThreeDots />
        </StyledButton>
      </div>
    </Dropdown>
  );

  return (
    <>
      {contextHolder}
      {dropDown}
    </>
  );
};

const mapStateToProps = createStructuredSelector({
  user: selectUser
});
const mapDispatchToProps = (dispatch) => ({
  openEditSavedListPopup: () => dispatch(ACTIONS.setFormPopups(true, formPopupsContentKeys.ADD_NEW_LIST)),
  populateSavedListPopup: (filterListData) => dispatch(ACTIONS.setFilterList({ ...filterListData, isEditView: true }))
});
const SavedListActionsMenu = compose(
  withTranslation(),
  connect(mapStateToProps, mapDispatchToProps)
)(ActionsMenuComponent);

export { SavedListActionsMenu };
